@if (session) {
  <div class="card">
    <div class="card-header compact">
      <h1>
        <span class="ellipsis" test-id="session-label" [fwTooltip]="session.label">
          {{ session.label || 'No Session Label' }}
        </span>
        <small test-id="session-timestamp">{{ timestamp }}</small>
      </h1>
      <div class="card-actions">
        <app-button-group class="small fill-white">
          @if (permissions.deleteContainer) {
            <button
              aria-label="Delete Session"
              mat-stroked-button
              test-id="delete-button"
              type="button"
              (click)="deleteSession()"
            >
              <mat-icon class="xs">delete</mat-icon>
            </button>
          }
          @if (permissions.viewAuditTrail) {
            <button
              aria-label="View Audit Trail"
              fwTooltip="View Audit Trail"
              mat-stroked-button
              test-id="audit-button"
              type="button"
              (click)="viewAuditTrail()"
            >
              <mat-icon class="xs">list</mat-icon>
            </button>
          }
          @if (permissions.editMetadata) {
            <button mat-stroked-button test-id="reset-button" type="button" (click)="resetForm()">Reset</button>
          }
        </app-button-group>
      </div>
    </div>
    <form [formGroup]="metadata">
      <div class="info-row">
        <span class="field-tooltip"></span>
        <span class="field-label">Label</span>
        <div class="field-value">
          @if (!permissions.editMetadata) {
            <span test-id="label-text">{{ session.label }}</span>
          }
          @if (permissions.editMetadata) {
            <input
              autocomplete="off"
              formControlName="label"
              placeholder="Enter label"
              test-id="label-input"
              type="text"
            />
          }
        </div>
        <div class="field-error">
          @if (metadata.get('label').hasError('required')) {
            <span>This field is required.</span>
          }
          @if (metadata.get('label').hasError('minlength')) {
            <span>Entry must be at least 2 characters in length.</span>
          }
          @if (metadata.get('label').hasError('maxlength')) {
            <span>Entry must be shorter than 64 characters.</span>
          }
        </div>
        <div class="field-action-menu">
          <fw-action-menu fieldKey="label" fieldLabel="Label" test-id="label-actions" [auditable]="session" />
        </div>
      </div>
      <div class="info-row">
        <span class="field-tooltip"></span>
        <span class="field-label">Timestamp</span>
        <div class="field-value">
          @if (!permissions.editMetadata) {
            <span test-id="timestamp-text">{{ timestamp }}</span>
          }
          @if (permissions.editMetadata) {
            <div class="datetime-field">
              <input
                autocomplete="off"
                formControlName="timestampDate"
                matInput
                placeholder="Select date"
                test-id="timestamp-date-input"
                [matDatepicker]="datepicker"
              />
              <mat-datepicker-toggle class="xs" test-id="timestamp-picker-toggle" [for]="datepicker">
                <mat-icon class="xs" matDatepickerToggleIcon>event</mat-icon>
              </mat-datepicker-toggle>
              <mat-datepicker #datepicker test-id="timestamp-picker" />
              @if (metadata.get('timestampDate').value) {
                <fw-time-field formControlName="timestampTime" test-id="timestamp-time-field" />
              }
              <app-typeahead
                class="timezone-typeahead full-width"
                formControlName="timezone"
                placeholder="Timezone"
                test-id="timezone-field"
                [options]="timezoneOptions"
              />
              @if (sessionService.showTimezone$.value) {
                <span style="margin: 0 5px" test-id="timezone-local">{{ localSessionTimestamp }}</span>
              }
            </div>
          }
        </div>
        <div class="field-error">
          @if (metadata.get('timestampDate').invalid) {
            <span>Date is invalid.</span>
          }
          @if (metadata.get('timestampDate').valid && metadata.get('timestampTime').invalid) {
            <span>Time is invalid.</span>
          }
        </div>
        <div class="field-action-menu">
          <fw-action-menu
            fieldKey="timestamp"
            fieldLabel="Timestamp"
            test-id="timestamp-actions"
            [auditable]="session"
          />
        </div>
      </div>
      <div class="info-row">
        <mat-icon class="field-tooltip xs" fwTooltip="At time of session">info</mat-icon>
        <span class="field-label">Age<small>(years)</small></span>
        <div class="field-value">
          @if (!permissions.editMetadata) {
            <span test-id="age-text">{{ ageInYears }}</span>
          }
          @if (permissions.editMetadata) {
            <input
              autocomplete="off"
              formControlName="age"
              min="0"
              placeholder="Enter age"
              test-id="age-input"
              type="number"
            />
          }
        </div>
        <div class="field-error">
          @if (metadata.get('age').invalid) {
            <span>Age must be a positive integer.</span>
          }
        </div>
        <div class="field-action-menu">
          <fw-action-menu fieldKey="age" fieldLabel="Age (years)" test-id="age-actions" [auditable]="session" />
        </div>
      </div>
      <div class="info-row">
        <mat-icon class="field-tooltip xs" fwTooltip="At time of session">info</mat-icon>
        <span class="field-label">Weight<small>(kg)</small></span>
        <div class="field-value">
          @if (!permissions.editMetadata) {
            <span test-id="weight-text">{{ session.weight }}</span>
          }
          @if (permissions.editMetadata) {
            <input
              autocomplete="off"
              formControlName="weight"
              min="0"
              placeholder="Enter weight"
              test-id="weight-input"
              type="number"
            />
          }
        </div>
        <div class="field-error">
          @if (metadata.get('weight').invalid) {
            <span>Weight must be a positive number.</span>
          }
        </div>
        <div class="field-action-menu">
          <fw-action-menu fieldKey="weight" fieldLabel="Weight (kg)" test-id="weight-actions" [auditable]="session" />
        </div>
      </div>
      <div class="info-row">
        <span class="field-tooltip"></span>
        <span class="field-label">Operator</span>
        <div class="field-value">
          @if (!permissions.editMetadata) {
            <span test-id="operator-text">{{ session.operator }}</span>
          }
          @if (permissions.editMetadata) {
            <input
              autocomplete="off"
              formControlName="operator"
              placeholder="Enter operator"
              test-id="operator-input"
              type="text"
            />
          }
        </div>
        <div class="field-error"></div>
        <div class="field-action-menu">
          <fw-action-menu fieldKey="operator" fieldLabel="Operator" test-id="operator-actions" [auditable]="session" />
        </div>
      </div>
    </form>
  </div>
}
